<script module lang="ts">
	import type { ISbStoryData, SbBlokData } from '@storyblok/js';
	import type { ImageFormat } from '../imageBuilder';

	export interface ImageBlock extends SbBlokData {
		component: 'image';
		asset: SbImage;
		desktop_asset: SbImage;
		aspect_ratio: SbImageFormat;
		desktop_aspect_ratio: SbImageFormat;
		rounded_corners: 'none' | '8-12' | '[20px]' | 'full' | 'xl' | '2xl';
		fit: 'contain' | 'cover';
		center_overlay: boolean;
		overlaying_blocks: ISbStoryData[];
		mirror: boolean;
		height: string;
		width: string;
		format?: Exclude<ImageFormat, null>;
	}
</script>

<script lang="ts">
	import Image from '../components/image.svelte';
	import type { SbImage, SbImageFormat } from '../types';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';

	interface Props {
		content: ISbStoryData<ImageBlock>['content'];
	}

	let { content }: Props = $props();

	// TODO: we can mirror the image via the CDN by adding a "-" in front of the resolution (instead of using a transform)
	let classes = $derived([
		`h-full w-full object-${content.fit || 'cover'} object-center`,
		{
			'rounded-lg lg:rounded-xl': content.rounded_corners === '8-12',
			[`rounded-${content.rounded_corners || 'none'}`]: content.rounded_corners !== '8-12',
			'scale-x-[-1]': content.mirror
		}
	]);

	let height = $derived(parseInt(content.height, 10));
	let width = $derived(parseInt(content.width, 10));
</script>

<div use:editable={content} class="relative z-0 h-full">
	{#if content.asset?.filename?.length > 0}
		<Image
			wrapperClass={content.desktop_asset?.filename?.length > 0 ? 'lg:hidden' : undefined}
			class={[
				...classes,
				`aspect-${content.aspect_ratio || 'auto'}`,
				{
					[`lg:aspect-${content.desktop_aspect_ratio}`]: content.desktop_aspect_ratio
				}
			]}
			src={content.asset.filename}
			alt={content.asset.alt}
			title={content.asset.title}
			width={width || 0}
			height={height || 0}
			format={content.format}
		/>
	{/if}
	{#if content.desktop_asset?.filename?.length > 0}
		<Image
			wrapperClass="max-lg:hidden"
			class={[
				...classes,
				`aspect-${content.desktop_aspect_ratio || content.aspect_ratio || 'auto'}`
			]}
			src={content.desktop_asset.filename}
			alt={content.desktop_asset.alt}
			title={content.desktop_asset.title}
			width={width || 0}
			height={height || 0}
			format={content.format}
		/>
	{/if}

	{#if content.overlaying_blocks?.length > 0}
		<div class={['absolute inset-0 z-10', { 'flex items-center': content.center_overlay }]}>
			{#each content.overlaying_blocks as block}
				<Blok content={block} />
			{/each}
		</div>
	{/if}
</div>
